import { useCallback, useEffect, useState } from "react";
import { useAccount, useChainId, useDisconnect } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { Button } from "@/components/common/Button";
import { logger } from "@/shared/utils/logger";
import { postAuthWalletConnect } from "@/modules/auth/api/user/connect-wallet";
import { useUserStore } from "@/modules/auth/store/user";
import { formatEllipsis } from "@/modules/auth/utils/format";

export const WalletConnect = () => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  const { openConnectModal } = useConnectModal();
  const { user, setUserOnly } = useUserStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isRelinkFlow, setIsRelinkFlow] = useState(false);
  const [hasTriedAutoLink, setHasTriedAutoLink] = useState(false);

  const hasLinkedWallet = !!user?.walletId;

  const linkWallet = useCallback(async () => {
    if (!address) return;

    try {
      setIsLoading(true);
      const res = await postAuthWalletConnect({
        wallet: address,
        network: "EVM",
        chainId: chainId.toString(),
      });

      if (!res) return;

      setUserOnly();
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [address, chainId, setUserOnly]);

  useEffect(() => {
    if (!hasLinkedWallet && isConnected && address && !hasTriedAutoLink) {
      setHasTriedAutoLink(true);
      void linkWallet();
    }
  }, [
    hasLinkedWallet,
    isConnected,
    address,
    chainId,
    hasTriedAutoLink,
    linkWallet,
    setUserOnly,
  ]);

  useEffect(() => {
    const relinkWallet = async () => {
      if (!address) return;

      try {
        setIsLoading(true);
        const res = await postAuthWalletConnect({
          wallet: address,
          network: "EVM",
          chainId: chainId.toString(),
        });

        if (!res) return;

        setIsRelinkFlow(false);
        setUserOnly();
      } catch (error) {
        logger.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isRelinkFlow && isConnected && address) {
      void relinkWallet();
    }
  }, [isRelinkFlow, isConnected, address, chainId, setUserOnly]);

  let buttonText: string;

  if (!hasLinkedWallet && !isConnected) {
    buttonText = "Connect BASE wallet";
  } else if (!hasLinkedWallet && isConnected) {
    buttonText = "Link BASE wallet to account";
  } else if (hasLinkedWallet && !isRelinkFlow) {
    buttonText = "Relink BASE wallet";
  } else {
    buttonText = "Link BASE wallet to account";
  }

  const handleClick = async () => {
    if (!hasLinkedWallet) {
      if (!isConnected) {
        openConnectModal?.();
      } else {
        setHasTriedAutoLink(false);
        await linkWallet();
      }
      return;
    }

    if (!isRelinkFlow) {
      disconnect();
      setIsRelinkFlow(true);
      openConnectModal?.();
      return;
    }

    if (!isConnected) {
      openConnectModal?.();
    }
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="pt-4 px-4 text-[0.8125rem] leading-[1rem] text-section-header-text tracking-tighter uppercase">
        {hasLinkedWallet
          ? "Your connected BASE wallet"
          : "Connect your BASE wallet to use it as your login method"}
      </p>

      {hasLinkedWallet && (
        <p className="px-4 text-[0.8125rem] leading-[1rem] text-section-header-text font-semibold tracking-tighter">
          {formatEllipsis(
            user.walletId.split(".")[user.walletId.split(".").length - 1],
            6,
            6
          )}
        </p>
      )}

      {buttonText !== "Relink BASE wallet" && (
        <Button
          onClick={handleClick}
          text={isLoading ? "Processing..." : buttonText}
          className="w-full"
          disabled={isLoading}
        />
      )}
    </div>
  );
};
